import React, { useState, useContext, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useNavigate, useLocation } from 'react-router-dom';
import { DoktorFooter } from "./components/DoktorFooter";
import { DoktorLinearBar } from "./components/DoktorLinearBar";
import { useClientApi } from "../../api/client";
import { CLIENT_TYPES } from "../../api/clientconfig";
import AuthContext from "../../context/AuthContext";
import dayjs from 'dayjs';
import { SimpleLoader } from "../../components/loading/simpleLoader";

/**
 * Validation limits for height and weight measurements
 */
const VALIDATION_LIMITS = {
    WEIGHT: {
        KG: { MIN: 30, MAX: 300 }
    },
    HEIGHT: {
        CM: { MIN: 120, MAX: 220 }
    }
};

/**
 * Default user data when no session ID is available
 */
const DEFAULT_USER_DATA = {
    age: 35,
    gender: "female",
    height: 170,
    weight: 70,
};

/**
 * DoktorDemographics component for collecting user health information
 * @returns {JSX.Element} The demographics collection form
 */
export const DoktorDemographics = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const clientApi = useClientApi( CLIENT_TYPES.DOKTOR_CONNECT );
    const theme = useTheme();
    const isLaptop = useMediaQuery( theme.breakpoints.up( 'lg' ) );
    const isSmallHeight = useMediaQuery( '(max-height:700px)' );

    // Ref to track if initial data has been fetched
    const dataFetched = useRef( false );

    // Component state
    const [ currentStep, setCurrentStep ] = useState( 0 );
    const [ isLoading, setIsLoading ] = useState( true );
    const [ error, setError ] = useState( null );
    const { setOrgUser } = useContext( AuthContext );
    const [ isFormComplete, setIsFormComplete ] = useState( false );
    const [ isSubmitting, setIsSubmitting ] = useState( false );

    // Get session_id from URL or localStorage
    const queryParams = new URLSearchParams( location.search );
    const sessionIdFromUrl = queryParams.get( 'session_id' );
    const sessionId = sessionIdFromUrl || localStorage.getItem( 'session-id' );

    // Flag to track if we're using the default values flow
    const [ isDefaultFlow, setIsDefaultFlow ] = useState( false );

    // Used to show error messages for validation
    const [ fieldErrors, setFieldErrors ] = useState( {
        ethnicity: ''
    } );

    // Initialize userData with minimal data rather than defaults
    const [ userData, setUserData ] = useState( {
        gender: '',
        genderId: null,
        age: null,
        dateOfBirth: null,
        smoker: null,
        weight: null,
        height: null,
        ethnicity: null,
        isFrontendHeightImperialUnits: false,
        isFrontendWeightImperialUnits: false,
        diabetes: '',
        isPregnant: null,
        isHighBP: '',
        hasHadHeartAttack: '',
        hasHadStroke: null,
        bpMeds: '',
    } );

    // Validation state
    const [ validation, setValidation ] = useState( {
        isEthnicityValid: false,
    } );

    /**
     * Validates user demographic data
     * @param {Object} userData - The user data to validate
     * @returns {Object} Validation result with isValid flag and validation details
     */
    const validateDemographicsData = ( userData ) => {
        // If we're using default flow, skip validation
        if ( isDefaultFlow ) {
            return { isValid: true, validation: {} };
        }

        const validation = {
            isDateOfBirthValid: { isTrue: true, errorMsg: "" },
            isHeightValid: { isTrue: true, errorMsg: "" },
            isWeightValid: { isTrue: true, errorMsg: "" },
            isBMIValid: { isTrue: true, errorMsg: "" }
        };

        let hasError = false;

        // Calculate age from DOB
        const calculateAge = ( birthDate ) => {
            return dayjs().diff( dayjs( birthDate ), 'year' );
        };

        // DOB Validation
        if ( !userData.dateOfBirth ) {
            validation.isDateOfBirthValid = {
                isTrue: false,
                errorMsg: "Please provide a valid date of birth."
            };
            hasError = true;
        } else {
            const age = calculateAge( userData.dateOfBirth );
            if ( age < 13 || age > 120 ) {
                validation.isDateOfBirthValid = {
                    isTrue: false,
                    errorMsg: "Age must be between 13 and 120 years."
                };
                hasError = true;
            }
        }

        // Height Validation (always in cm)
        if ( !userData.height ) {
            validation.isHeightValid = {
                isTrue: false,
                errorMsg: "Please provide a valid height."
            };
            hasError = true;
        } else {
            const heightCm = Number( userData.height );
            const { MIN, MAX } = VALIDATION_LIMITS.HEIGHT.CM;

            if ( isNaN( heightCm ) || heightCm < MIN || heightCm > MAX ) {
                validation.isHeightValid = {
                    isTrue: false,
                    errorMsg: `Height must be between ${ MIN }cm and ${ MAX }cm.`
                };
                hasError = true;
            }
        }

        // Weight Validation (always in kg)
        if ( !userData.weight ) {
            validation.isWeightValid = {
                isTrue: false,
                errorMsg: "Please provide a valid weight."
            };
            hasError = true;
        } else {
            const weightKg = Number( userData.weight );
            const { MIN, MAX } = VALIDATION_LIMITS.WEIGHT.KG;

            if ( isNaN( weightKg ) || weightKg < MIN || weightKg > MAX ) {
                validation.isWeightValid = {
                    isTrue: false,
                    errorMsg: `Weight must be between ${ MIN }kg and ${ MAX }kg.`
                };
                hasError = true;
            }
        }

        // BMI Validation
        if ( validation.isHeightValid.isTrue && validation.isWeightValid.isTrue ) {
            const heightInMeters = Number( userData.height ) / 100;
            const weightInKg = Number( userData.weight );
            const bmi = weightInKg / ( heightInMeters * heightInMeters );

            if ( bmi < 10 || bmi > 65 ) {
                const minWeight = Math.round( 10 * ( heightInMeters * heightInMeters ) );
                const maxWeight = Math.round( 65 * ( heightInMeters * heightInMeters ) );

                validation.isBMIValid = {
                    isTrue: false,
                    errorMsg: `Invalid BMI. For your height of ${ userData.height }cm, weight should be between ${ minWeight }kg and ${ maxWeight }kg.`
                };
                hasError = true;
            }
        }

        return {
            isValid: !hasError,
            validation
        };
    };

    // Fetch existing assessment data if session_id exists - only once
    useEffect( () => {
        const fetchAssessmentData = async () => {
            // Skip if data has already been fetched
            if ( dataFetched.current ) return;

            if ( sessionIdFromUrl ) {
                // There's a session ID in URL, fetch existing data
                try {
                    setIsLoading( true );
                    const response = await clientApi.getAssessmentBySession( sessionIdFromUrl );

                    if ( response?.data?.assessment ) {
                        localStorage.setItem( 'doktorConnect-assessment-id', response.data.assessment.id );
                        localStorage.setItem( 'doktor-redirect-url', response.data.assessment.redirect_url );
                    }

                    if ( response?.data?.demographics ) {
                        const demo = response.data.demographics;
                        // Update userData with existing demographics
                        setUserData( prev => ( {
                            ...prev,
                            dateOfBirth: demo.date_of_birth ? dayjs( demo.date_of_birth ) : null,
                            genderId: demo.gender_id || null,
                            height: demo.height || null,
                            weight: demo.weight || null,
                            // Add other fields as needed
                        } ) );
                    }

                    // Mark data as fetched to prevent further requests
                    dataFetched.current = true;
                } catch ( error ) {
                    setError( "Failed to load existing data. You may continue with new entries." );
                    // Mark as fetched even on error to avoid infinite retry
                    dataFetched.current = true;
                } finally {
                    setIsLoading( false );
                }
            } else {
                // No session ID in URL - mark we're using default flow
                setIsDefaultFlow( true );
                setIsLoading( false );
                // Mark as fetched since there's no session ID
                dataFetched.current = true;
            }
        };

        fetchAssessmentData();

        // If session ID changes, allow refetching
        return () => {
            dataFetched.current = false;
        };
    }, [ sessionIdFromUrl ] );

    /**
     * Handles advancing to the next step
     */
    const handleNextStep = () => {
        setCurrentStep( prev => prev + 1 );
    };

    /**
     * Handles selection of an option in the form
     * @param {string} field - The field being updated
     * @param {any} value - The selected value
     */
    const handleOptionSelect = ( field, value ) => {
        setUserData( prev => ( {
            ...prev,
            [ field ]: value
        } ) );

        // Adjust step progression logic
        if ( currentStep === 1 && field === "smoker" ) {
            handleNextStep();
        } else if ( currentStep === 3 && field === "isHighBP" && !value ) {
            setCurrentStep( prev => prev + 2 );
            setUserData( prev => ( {
                ...prev,
                bpMeds: false
            } ) );
        } else if ( currentStep === 7 && field === 'isPregnant' ) {
            // For the pregnant step, immediately set form as complete and trigger submission
            setUserData( prev => ( {
                ...prev,
                isPregnant: value
            } ) );
            setIsFormComplete( true );
        } else {
            handleNextStep();
        }

        // Clear any existing error for this field
        if ( fieldErrors[ field ] ) {
            setFieldErrors( prev => ( {
                ...prev,
                [ field ]: ''
            } ) );
        }
    };

    // Ref to track submission state to avoid re-renders
    const submissionAttempted = useRef( false );

    // Handle form submission once all data is collected
    useEffect( () => {
        if ( isFormComplete && !isSubmitting && !submissionAttempted.current ) {
            submissionAttempted.current = true;
            handleSubmit();
        }
    }, [ isFormComplete, isSubmitting ] );

    /**
     * Creates a demographics object from user data
     * @param {Object} userData - The user data to format
     * @returns {Object} Formatted demographics object for API
     */
    const createDemographicsObject = ( userData ) => {
        return {
            age: userData.age,
            birthDate: userData.dateOfBirth,
            gender: userData.genderId,
            height: userData.height,
            weight: userData.weight,
            isFrontendHeightImperialUnits: userData.isFrontendHeightImperialUnits,
            isFrontendWeightImperialUnits: userData.isFrontendWeightImperialUnits,
            ethnicityId: userData.ethnicity,
            isSmoke: userData.smoker,
            isDiabetes: userData.diabetes,
            hasHighBP: userData.isHighBP,
            hasHadStroke: userData.hasHadStroke,
            hasHadAttack: userData.hasHadHeartAttack,
            isOnBpMeds: userData.bpMeds,
            isPregnant: userData.isPregnant,
            hasMedicalCondition: null,
            medicalConditionNames: null,
            hasAllergies: null,
            allergyNames: null,
            hasSupplements: null,
            supplements: null,
            wellnessGoals: null,
        };
    };

    /**
     * Handles form submission
     */
    const handleSubmit = async () => {
        if ( isSubmitting ) return;

        setIsSubmitting( true );
        setError( null ); // Clear any previous errors

        try {
            setIsLoading( true );

            if ( isDefaultFlow ) {
                // Convert default age to date of birth
                const currentYear = dayjs().year();
                const birthYear = currentYear - DEFAULT_USER_DATA.age;
                const dateOfBirth = dayjs().year( birthYear ).startOf( 'year' ).toISOString();

                // Set default values in userData
                const updatedUserData = {
                    ...userData,
                    age: DEFAULT_USER_DATA.age,
                    dateOfBirth: dateOfBirth,
                    gender: DEFAULT_USER_DATA.gender,
                    genderId: DEFAULT_USER_DATA.gender === "female" ? 2 : 1,
                    height: DEFAULT_USER_DATA.height,
                    weight: DEFAULT_USER_DATA.weight
                };

                setUserData( updatedUserData );

                // Add user-selected data
                const demographics2 = createDemographicsObject( updatedUserData );

                // Transform data to match API expectations
                const demographics = {
                    age: Number( DEFAULT_USER_DATA.age ),
                    gender: DEFAULT_USER_DATA.gender,
                    height: Number( DEFAULT_USER_DATA.height ),
                    weight: Number( DEFAULT_USER_DATA.weight )
                };

                // Post demographics to get session ID
                const response = await clientApi.postDemographics( demographics );

                if ( response?.data ) {
                    // Save the received session ID and redirect URL
                    const newSessionId = response.data.session_id;
                    localStorage.setItem( 'session-id', newSessionId );
                    localStorage.setItem( 'doktor-redirect-url', response.data.redirect_url );

                    // Set session for auth context
                    setOrgUser( newSessionId );

                    // Now post the complete demographics with the user-selected data
                    const assessmentId = response.data.assessment_id || '';
                    localStorage.setItem( 'doktorConnect-assessment-id', assessmentId );

                    // Post the full demographics with user selections
                    const demoResponse = await clientApi.postDemographicsNew( demographics2, assessmentId );

                    if ( demoResponse?.data?.result === 'saved' ) {
                        // Navigate to next step without URL parameter
                        navigate( '/doktor-pre-face-scan' );
                    } else {
                        throw new Error( 'Invalid demographics response structure' );
                    }
                } else {
                    throw new Error( 'Invalid API response structure' );
                }
            } else {
                // Normal flow with existing session ID
                // Validate the form
                const { isValid, validation } = validateDemographicsData( userData );

                // If validation fails, set error messages
                if ( !isValid ) {
                    const errorMessages = Object.values( validation )
                        .filter( v => v.errorMsg )
                        .map( v => v.errorMsg );

                    // Combine all error messages into a single error string
                    const combinedErrorMessage = errorMessages.join( " " );

                    setError( combinedErrorMessage || 'Please check all required fields' );
                    setIsSubmitting( false );
                    setIsFormComplete( false );
                    submissionAttempted.current = false;
                    setIsLoading( false );
                    return;
                }

                if ( sessionId ) {
                    localStorage.setItem( 'session-id', sessionId );
                    setOrgUser( sessionId );
                }

                const demographics = createDemographicsObject( userData );
                const assessmentId = localStorage.getItem( 'doktorConnect-assessment-id' ) || '';
                const response = await clientApi.postDemographicsNew( demographics, assessmentId );

                // Check if the API response indicates success
                if ( response?.data?.result === 'saved' ) {
                    // Navigate with session_id as a URL parameter
                    navigate( `/doktor-pre-face-scan${ sessionId ? `?session_id=${ sessionId }` : '' }` );
                } else {
                    throw new Error( 'Invalid API response structure' );
                }
            }
        } catch ( err ) {
            setError( 'Failed to submit demographics. Please try again.' );
            setIsFormComplete( false );
            submissionAttempted.current = false;
        } finally {
            setIsLoading( false );
            setIsSubmitting( false );
        }
    };

    /**
     * Renders validation errors
     * @returns {JSX.Element|null} Error messages box or null
     */
    const renderValidationErrors = () => {
        const { validation } = validateDemographicsData( userData );
        const errorMessages = Object.values( validation )
            .filter( v => v.errorMsg )
            .map( v => v.errorMsg );

        return errorMessages.length > 0 ? (
            <Box sx={ { mt: 2, p: 2, backgroundColor: '#FFEBEE', borderRadius: '4px' } }>
                { errorMessages.map( ( msg, index ) => (
                    <Typography key={ index } color="error" align="left" sx={ { mb: 1 } }>
                        { msg }
                    </Typography>
                ) ) }
            </Box>
        ) : null;
    };

    /**
     * Renders the diabetes step
     * @returns {JSX.Element} Diabetes step UI
     */
    const renderDiabetesStep = () => (
        <StepContainer title="Are you diabetic?">
            { [
                { label: 'Type 1', value: 1 },
                { label: 'Type 2', value: 2 },
                { label: 'No', value: 3 }
            ].map( ( option, index ) => (
                <OptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( 'diabetes', option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    /**
     * Renders the smoking step
     * @returns {JSX.Element} Smoking step UI
     */
    const renderSmokingStep = () => (
        <StepContainer title="Do you smoke?">
            { [
                { label: 'Yes', value: true },
                { label: 'No', value: false }
            ].map( ( option, index ) => (
                <OptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( 'smoker', option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    /**
     * Renders the ethnicity step
     * @returns {JSX.Element} Ethnicity step UI
     */
    const renderEthnicityStep = () => (
        <StepContainer title="What is your ethnicity?">
            <Box sx={ {
                maxHeight: isLaptop ? 'none' : 'calc(100vh - 300px)',
                overflowY: isLaptop ? 'visible' : 'auto',
                maxWidth: '650px',
                paddingRight: '10px',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#0633CB14',
                    borderRadius: '4px',
                },
            } }>
                { [
                    { label: 'American Indian or Alaska Native', value: 1 },
                    { label: 'Asian', value: 2 },
                    { label: 'Black or African American', value: 3 },
                    { label: 'Hispanic or Latino', value: 4 },
                    { label: 'Native Hawaiian or Pacific Islander', value: 5 },
                    { label: 'White', value: 6 },
                    { label: 'Mixed', value: 7 },
                    { label: 'Prefer not to say', value: 8 }
                ].map( ( option, index ) => (
                    <OptionButton
                        key={ index }
                        option={ option.label }
                        onClick={ () => {
                            // Explicitly set ethnicity as the value
                            setUserData( prev => ( {
                                ...prev,
                                ethnicity: option.value
                            } ) );
                            // Set form complete for next step
                            handleNextStep();
                        } }
                    />
                ) ) }
            </Box>
        </StepContainer>
    );

    /**
     * Renders the high blood pressure step
     * @returns {JSX.Element} High BP step UI
     */
    const renderHighBPStep = () => (
        <StepContainer title="Do you have high blood pressure?">
            { [
                { label: 'Yes', value: true },
                { label: 'No', value: false }
            ].map( ( option, index ) => (
                <OptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( 'isHighBP', option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    /**
     * Renders the blood pressure medication step
     * @returns {JSX.Element} BP meds step UI
     */
    const renderBPMedsStep = () => (
        <StepContainer title="Are you taking high blood pressure medication?">
            { [
                { label: 'Yes', value: true },
                { label: 'No', value: false }
            ].map( ( option, index ) => (
                <OptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( 'bpMeds', option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    /**
     * Renders the heart attack history step
     * @returns {JSX.Element} Heart attack step UI
     */
    const renderHeartAttackStep = () => (
        <StepContainer title="Have you had a heart attack before?">
            { [
                { label: 'Yes', value: true },
                { label: 'No', value: false }
            ].map( ( option, index ) => (
                <OptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( 'hasHadHeartAttack', option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    /**
     * Renders the stroke history step
     * @returns {JSX.Element} Stroke step UI
     */
    const renderStrokeStep = () => (
        <StepContainer title="Have you had a stroke before?">
            { [
                { label: 'Yes', value: true },
                { label: 'No', value: false }
            ].map( ( option, index ) => (
                <OptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( 'hasHadStroke', option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    /**
     * Renders the pregnancy/breastfeeding step
     * @returns {JSX.Element} Pregnancy step UI
     */
    const renderPregnantStep = () => (
        <StepContainer title="Are you pregnant or breastfeeding?">
            { [
                { label: 'Yes, I am pregnant.', value: true },
                { label: 'Yes, I am breastfeeding.', value: true },
                { label: 'No', value: false }
            ].map( ( option, index ) => (
                <OptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( 'isPregnant', option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    /**
     * Renders the current step based on currentStep value
     * @returns {JSX.Element} The current step UI
     */
    const renderStep = () => {
        switch ( currentStep ) {
            case 0:
                return renderDiabetesStep();
            case 1:
                return renderSmokingStep();
            case 2:
                return renderEthnicityStep();
            case 3:
                return renderHighBPStep();
            case 4:
                return renderBPMedsStep();
            case 5:
                return renderHeartAttackStep();
            case 6:
                return renderStrokeStep();
            case 7:
                return renderPregnantStep();
            default:
                return null;
        }
    };

    return (
        <>
            { isLoading ? (
                <SimpleLoader textColor="#232A67" loadingText="Loading..."></SimpleLoader>
            ) : (
                <Box
                    sx={ {
                        height: isLaptop ? '100vh' : ( isSmallHeight ? "85vh" : "90vh" ),
                        display: 'flex',
                        flexDirection: 'column',
                        maxWidth: isLaptop ? '1200px' : '800px',
                        margin: '0 auto',
                        padding: isLaptop ? '24px' : '16px',
                        boxSizing: 'border-box',
                    } }>
                    <Box
                        className="doktor-symptoms"
                        sx={ {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            maxWidth: isLaptop ? '900px' : '390px',
                            width: '100%',
                            margin: '0 auto',
                        } }>
                        {/* Header */ }
                        <Box className="doktor-header" height={ isLaptop ? 50 : 40 }>
                            <DoktorLinearBar value={ ( currentStep + 1 ) * ( 100 / 12 ) } />
                        </Box>

                        {/* Error display for validation issues */ }
                        { error && (
                            <Box sx={ { mt: 2, p: 2, backgroundColor: '#FFEBEE', borderRadius: '4px' } }>
                                <Typography color="error" align="center">
                                    { error }
                                </Typography>
                            </Box>
                        ) }

                        {/* Body */ }
                        <Box
                            className="doktor-body"
                            flex={ 1 }
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            overflow="auto"
                            marginTop={ isLaptop ? ( currentStep === 0 ? 2 : 4 ) : 2 }
                            paddingX={ isLaptop ? 10 : 5 }
                        >
                            { renderStep() }
                        </Box>

                        {/* Footer */ }
                        <Box textAlign="center" mt="auto" mb={ 2 } maxWidth='100%' display='flex' justifyContent='center' overflowX='hidden'>
                            <DoktorFooter />
                        </Box>
                    </Box>
                </Box>
            ) }
        </>
    );
};

/**
 * Container component for each step
 * @param {Object} props - Component props
 * @param {string} props.title - Step title
 * @param {React.ReactNode} props.children - Step content
 * @returns {JSX.Element} Step container UI
 */
const StepContainer = ( { title, children } ) => {
    const isLaptop = useMediaQuery( ( theme ) => theme.breakpoints.up( 'lg' ) );
    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems={ title === 'Terms & Conditions' ? 'start' : 'center' }
            minHeight={ isLaptop ? 'auto' : '100%' }
        >
            <Typography
                color="#232A67"
                display='block'
                marginBottom={ isLaptop ? 4 : 2 }
                sx={ {
                    fontSize: isLaptop ? '28px' : { xs: '23px', sm: '23px' },
                    fontWeight: '700',
                    lineHeight: isLaptop ? '34px' : { xs: '27px', sm: '27px' },
                    textAlign: 'center',
                } }>
                { title }
            </Typography>
            { children }
        </Box>
    );
};

/**
 * Button component for step options
 * @param {Object} props - Component props
 * @param {string} props.option - Option text
 * @param {Function} props.onClick - Click handler
 * @returns {JSX.Element} Option button UI
 */
const OptionButton = ( { option, onClick } ) => {
    const isLaptop = useMediaQuery( ( theme ) => theme.breakpoints.up( 'lg' ) );
    const isMobile = useMediaQuery( ( theme ) => theme.breakpoints.down( 'sm' ) );

    return (
        <Button
            fullWidth
            disableRipple={ isMobile } // Disable ripple effect on mobile
            sx={ {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: isLaptop ? '52px' : { xs: '44px', sm: '44px' },
                background: '#0633CB14',
                borderRadius: '10px',
                marginBottom: "10px",
                transition: "all 0.3s ease",
                // Only apply hover effect on non-mobile devices
                ...( !isMobile && {
                    "&:hover": {
                        backgroundColor: "#0633CB4D",
                    },
                } ),
                // Maintain light blue background in all states on mobile
                ...( isMobile && {
                    "&:hover": {
                        backgroundColor: '#0633CB14',
                    },
                    "&:active": {
                        backgroundColor: '#0633CB14',
                    },
                    "&:focus": {
                        backgroundColor: '#0633CB14',
                    },
                    "&.Mui-focused": {
                        backgroundColor: '#0633CB14',
                    },
                    "&.Mui-touched": {
                        backgroundColor: '#0633CB14',
                    },
                    "&.MuiButtonBase-root": {
                        backgroundColor: '#0633CB14 !important',
                    }
                } ),
            } }
            onClick={ ( e ) => {
                e.currentTarget.blur();
                onClick();
            } }
        >
            <Typography
                color="#000"
                display='block'
                sx={ {
                    fontSize: isLaptop ? '18px' : { xs: '15px', sm: '15px' },
                    fontWeight: '500',
                    lineHeight: isLaptop ? '24px' : { xs: '22px', sm: '22px' },
                    textAlign: 'center',
                } }>
                { option }
            </Typography>
        </Button>
    );
};